import { ArrowRight01Icon, Cancel01Icon, Pin02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { parseFieldChange, parseHistoryJSON } from 'constants/check-history-actions';
import { CheckHistoryTypes } from 'constants/check-history-types';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';

import { Drawer } from 'components/Drawer/Drawer';
import { Button } from 'components/FormComponents';
import historyRowStyles from 'components/HistoryRow/HistoryRow.module.scss';

import styles from './HistoryDetailsPanel.module.scss';

const ACTION_LABEL_KEY_BY_TYPE = {
  [CheckHistoryTypes.NewCheck]: 'newCheck',
  [CheckHistoryTypes.BackgroundCheckSubmitted]: 'backgroundCheckSubmitted',
  [CheckHistoryTypes.BackgroundCheckProcessing]: 'backgroundCheckProcessing',
  [CheckHistoryTypes.BackgroundCheckCompleted]: 'backgroundCheckCompleted',
  [CheckHistoryTypes.BackgroundCheckUpdated]: 'backgroundCheckUpdated',
  [CheckHistoryTypes.SubCheckCompleted]: 'subCheckCompleted',
};

// Field-change types carry their own {field, from, to} payload in entry.message —
// no order-form/client_fields cross-reference needed for these.
const FIELD_CHANGE_TYPES = new Set([
  CheckHistoryTypes.UpdatedEmail,
  CheckHistoryTypes.UpdatedContactNumber,
  CheckHistoryTypes.UpdatedRefereeDetails,
  CheckHistoryTypes.UpdatedCandidateDetails,
  CheckHistoryTypes.UpdatedDepartment,
  CheckHistoryTypes.UpdatedJobRole,
  CheckHistoryTypes.UpdatedCandidateTeam,
  CheckHistoryTypes.TransferUserAssignment,
  CheckHistoryTypes.UpdatedCandidateNotificationList,
]);

/**
 * Given a client_field definition and a raw value from check.details,
 * returns a human-readable display string.
 */
const resolveDisplayValue = (field, raw) => {
  if (raw === null || raw === undefined || raw === '') return '—';

  // Boolean-like values
  if (typeof raw === 'boolean') return raw ? 'Yes' : 'No';
  if (raw === 'yes' || raw === 'true') return 'Yes';
  if (raw === 'no' || raw === 'false') return 'No';

  // For select/radio fields: map option value → option name
  const optionsList = Array.isArray(field?.options)
    ? field.options
    : Array.isArray(field?.options?.options)
      ? field.options.options
      : [];

  if (optionsList.length > 0) {
    // Multi-select: raw may be an array of values
    if (Array.isArray(raw)) {
      const names = raw.map(v => optionsList.find(o => o.value === v)?.name ?? v);
      return names.join(', ');
    }
    const match = optionsList.find(o => o.value === raw);
    if (match) return match.name;
  }

  // Array of plain strings or objects
  if (Array.isArray(raw)) {
    return raw
      .map(item =>
        typeof item === 'object' && item !== null ? JSON.stringify(item) : String(item),
      )
      .join(', ');
  }

  return String(raw);
};

/** A single "was → now" row, used for both single- and multi-field change displays. */
const FieldChangeRow = ({ field, from, to }) => (
  <div className="flex flex-col gap-2">
    {field && <p className="text-text-secondary text-lg">{field}</p>}
    <div className="flex items-center gap-3 flex-wrap">
      <span className="text-text-tertiary line-through decoration-2">{from || '—'}</span>
      <HugeiconsIcon icon={ArrowRight01Icon} size="18" className="text-text-tertiary shrink-0" />
      <span className="text-text-primary font-semibold">{to || '—'}</span>
    </div>
  </div>
);

/** Added/removed list-membership changes (e.g. notification list emails), used instead of
 * FieldChangeRow when the payload carries `added`/`removed` arrays rather than a single from/to. */
const ListMembershipChangeRow = ({ field, added = [], removed = [] }) => (
  <div className="flex flex-col gap-4">
    {field && <p className="text-text-secondary text-lg">{field}</p>}
    {added.length > 0 && (
      <div className="flex flex-col gap-2">
        <p className="text-text-secondary text-base uppercase tracking-widest">Added</p>
        <div className="flex flex-col gap-1">
          {added.map(email => (
            <span key={email} className="text-text-primary font-semibold">
              + {email}
            </span>
          ))}
        </div>
      </div>
    )}
    {removed.length > 0 && (
      <div className="flex flex-col gap-2">
        <p className="text-text-secondary text-base uppercase tracking-widest">Removed</p>
        <div className="flex flex-col gap-1">
          {removed.map(email => (
            <span key={email} className="text-text-tertiary line-through decoration-2">
              {email}
            </span>
          ))}
        </div>
      </div>
    )}
  </div>
);

export const HistoryDetailsPanel = ({
  open,
  onClose,
  onExited,
  entry,
  check,
  companyCheck,
  checkHuman,
  candidate,
  pinned = false,
  onTogglePin,
}) => {
  const { t } = useTranslation();

  if (!entry) return null;

  const fieldChange = FIELD_CHANGE_TYPES.has(entry.type) ? parseFieldChange(entry.message) : null;
  const candidateChanges =
    entry.type === CheckHistoryTypes.CandidateUpdatedRefereeDetails
      ? parseHistoryJSON(entry.message)
      : null;
  const candidateReplace =
    entry.type === CheckHistoryTypes.CandidateReplacedReferee
      ? parseHistoryJSON(entry.message)
      : null;
  const hasCustomDetails = !!(fieldChange || candidateChanges || candidateReplace);

  const clientFields = companyCheck?.check_type?.client_fields ?? [];

  // Build a map of code → field definition for label + option lookups
  const fieldMap = clientFields.reduce((acc, field) => {
    acc[field.code] = field;
    return acc;
  }, {});

  // Filter out internal/empty details and order them by client_fields definition
  const detailsEntries = (() => {
    if (!check?.details) return [];
    const details = check.details;

    // Show fields in the order they appear in client_fields, then any extras
    const ordered = clientFields
      .filter(
        f => details[f.code] !== undefined && details[f.code] !== null && details[f.code] !== '',
      )
      .map(f => [f.code, details[f.code]]);

    const definedCodes = new Set(clientFields.map(f => f.code));
    const extras = Object.entries(details).filter(
      ([k, v]) => !definedCodes.has(k) && v !== null && v !== undefined && v !== '',
    );

    return [...ordered, ...extras];
  })();

  const actionLabelKey = ACTION_LABEL_KEY_BY_TYPE[entry.type];
  const actionLabel = actionLabelKey
    ? t(`historyDetailsPanel.actionLabels.${actionLabelKey}`)
    : null;

  return (
    <Drawer open={open} onClose={onClose} onExited={onExited} title="Details">
      <Drawer.Body>
        {/* ── Entry summary card (text-2xl to match HistoryRow) ── */}
        <div className="bg-neutral-2 rounded-2xl p-8 mb-8 flex flex-col gap-5 text-2xl shadow-[inset_0_2px_4px_0_rgba(0,0,0,0.06)]">
          {/* User row + Pinned badge + Pin/Unpin */}
          <div className="flex items-start justify-between gap-4 text-2xl">
            <div>
              <p className="text-text-secondary text-base uppercase tracking-widest mb-2">User</p>
              <p className="text-text-primary font-medium">@{entry.user_name ?? 'User'}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {onTogglePin &&
                (pinned ? (
                  <Button
                    className={cn(
                      historyRowStyles.pinBtn,
                      historyRowStyles.pinBtnActive,
                      styles.pinBtnActiveInline,
                    )}
                    onClick={() => onTogglePin(entry)}
                    tip="Unpin">
                    <span className={historyRowStyles.pinBtnDefaultIcon}>
                      <HugeiconsIcon
                        icon={Pin02Icon}
                        size="20"
                        strokeWidth={1.5}
                        className={historyRowStyles.pinBtnActiveIcon}
                      />
                    </span>
                    <span className={historyRowStyles.pinBtnHoverIcon}>
                      <HugeiconsIcon icon={Cancel01Icon} size="20" strokeWidth={1.5} />
                    </span>
                  </Button>
                ) : (
                  <Button
                    className={historyRowStyles.pinBtn}
                    onClick={() => onTogglePin(entry)}
                    tip="Pin">
                    <HugeiconsIcon icon={Pin02Icon} size="20" strokeWidth={1.5} />
                  </Button>
                ))}
            </div>
          </div>

          {/* Action */}
          {actionLabel && (
            <div>
              <p className="text-text-secondary text-base uppercase tracking-widest mb-2">Action</p>
              <p className="text-text-primary">{actionLabel}</p>
            </div>
          )}

          {/* Service */}
          {checkHuman && (
            <div>
              <p className="text-text-secondary text-base uppercase tracking-widest mb-2">
                Service
              </p>
              <p className="text-text-primary font-medium">{checkHuman}</p>
            </div>
          )}

          {/* Target */}
          {candidate && (
            <div>
              <p className="text-text-secondary text-base uppercase tracking-widest mb-2">Target</p>
              <p className="text-text-primary">for {candidate.name}</p>
            </div>
          )}

          {/* Date + Time */}
          <div className="flex gap-10">
            <div>
              <p className="text-text-secondary text-base uppercase tracking-widest mb-2">Date</p>
              <p className="text-text-primary">
                <Moment format="DD/MM/YYYY">{entry.created_at}</Moment>
              </p>
            </div>
            <div>
              <p className="text-text-secondary text-base uppercase tracking-widest mb-2">Time</p>
              <p className="text-text-primary">
                <Moment format="HH:mm">{entry.created_at}</Moment>
              </p>
            </div>
          </div>
        </div>

        {/* ── Additional Information (field change: from/to) ── */}
        {fieldChange && (
          <div className="text-2xl">
            <h3 className="font-sans text-neutral-7 text-3xl font-semibold mb-6">
              Additional Information
            </h3>
            <div className="bg-neutral-2 border border-border-primary rounded-2xl p-6 flex flex-col gap-5">
              <p className="text-text-secondary text-base uppercase tracking-widest">
                What changed
              </p>
              {fieldChange.added || fieldChange.removed ? (
                <ListMembershipChangeRow
                  field={fieldChange.field}
                  added={fieldChange.added}
                  removed={fieldChange.removed}
                />
              ) : (
                <FieldChangeRow
                  field={fieldChange.field}
                  from={fieldChange.from}
                  to={fieldChange.to}
                />
              )}
            </div>
          </div>
        )}

        {/* ── Additional Information (candidate-initiated multi-field edit) ── */}
        {candidateChanges && (
          <div className="text-2xl">
            <h3 className="font-sans text-neutral-7 text-3xl font-semibold mb-6">
              Additional Information
            </h3>
            <div className="flex flex-col gap-6">
              <div className="bg-neutral-2 border border-border-primary rounded-2xl p-6 flex flex-col gap-5">
                <p className="text-text-secondary text-base uppercase tracking-widest">
                  What changed
                </p>
                <div className="flex flex-col gap-5">
                  {(candidateChanges.changes ?? []).map((change, index) => (
                    <div
                      key={`${change.field}-${index}`}
                      className={cn(index > 0 && 'pt-5 border-t border-border-primary')}>
                      <FieldChangeRow field={change.field} from={change.from} to={change.to} />
                    </div>
                  ))}
                </div>
              </div>
              {candidateChanges.reason && (
                <div className="flex flex-col gap-2">
                  <p className="text-text-secondary text-xl">Reason</p>
                  <p className="text-text-primary font-medium">{candidateChanges.reason}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Additional Information (candidate-initiated replace: shows the submitted form, not just a basic detail list) ── */}
        {candidateReplace && (
          <div className="text-2xl">
            <h3 className="font-sans text-neutral-7 text-3xl font-semibold mb-6">
              Additional Information
            </h3>
            <div className="flex flex-col gap-6">
              <div className="bg-neutral-2 border border-border-primary rounded-2xl p-6 flex flex-col gap-5">
                <p className="text-text-secondary text-base uppercase tracking-widest">
                  Previous referee — {candidateReplace.old_referee}
                </p>
                {candidateReplace.old_referee_fields &&
                Object.keys(candidateReplace.old_referee_fields).length > 0 ? (
                  <div className="grid grid-cols-2 gap-5">
                    {Object.entries(candidateReplace.old_referee_fields).map(([label, value]) => (
                      <div key={label} className="flex flex-col gap-1 col-span-1">
                        <p className="text-text-secondary text-lg">{label}</p>
                        <p className="text-text-primary font-medium">{value || '—'}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-text-primary font-medium">{candidateReplace.old_referee}</p>
                )}
              </div>

              <div className="bg-neutral-2 border border-border-primary rounded-2xl p-6 flex flex-col gap-5">
                <p className="text-text-secondary text-base uppercase tracking-widest">
                  New referee — submitted form
                </p>
                <div className="grid grid-cols-2 gap-5">
                  {Object.entries(candidateReplace.fields ?? {}).map(([label, value]) => (
                    <div key={label} className="flex flex-col gap-1 col-span-1">
                      <p className="text-text-secondary text-lg">{label}</p>
                      <p className="text-text-primary font-medium">{value || '—'}</p>
                    </div>
                  ))}
                </div>
              </div>

              {candidateReplace.reason && (
                <div className="flex flex-col gap-2">
                  <p className="text-text-secondary text-xl">Reason</p>
                  <p className="text-text-primary font-medium">{candidateReplace.reason}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Additional Information (order form fields) ── */}
        {!hasCustomDetails && detailsEntries.length > 0 && (
          <div className="text-2xl">
            <h3 className="font-sans text-neutral-7 text-3xl font-semibold mb-6">
              Additional Information
            </h3>
            <div className="flex flex-col gap-8">
              {detailsEntries.map(([code, value]) => {
                const field = fieldMap[code];
                return (
                  <div key={code} className="flex flex-col gap-2">
                    <p className="text-text-secondary text-xl">{field?.title ?? code}</p>
                    <p className="text-text-primary font-medium">
                      {resolveDisplayValue(field, value)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Check loaded but no non-empty detail fields (empty/null details or all values blank) */}
        {!hasCustomDetails && check && detailsEntries.length === 0 && (
          <div className="text-2xl">
            <h3 className="font-sans text-neutral-7 text-3xl font-semibold mb-6">
              Additional Information
            </h3>
            <p className="text-text-secondary text-2xl">No additional information available.</p>
          </div>
        )}

        {/* Loading skeleton when entry suggests check data but it isn't loaded yet */}
        {!hasCustomDetails && (entry.candidate_check_id || entry.check_type) && !check && (
          <div className="text-2xl">
            <h3 className="font-sans text-neutral-7 text-3xl font-semibold mb-6">
              Additional Information
            </h3>
            <div className="flex flex-col gap-8 animate-pulse">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex flex-col gap-2">
                  <div className="h-5 w-32 bg-neutral-3 rounded" />
                  <div className="h-6 w-full max-w-xs bg-neutral-3 rounded" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state when no check data and not loading */}
        {!hasCustomDetails && !check && !entry.candidate_check_id && !entry.check_type && (
          <p className="text-text-secondary text-2xl">No additional information available.</p>
        )}
      </Drawer.Body>
    </Drawer>
  );
};
